/** The draw buckets on `$device_id`, which survives `identify()`, so the arm holds across signup.
 *  For a visitor PostHog has never seen, the proxy mints the id and hands it to posthog-js in `mc_ph_did`. */

import { readCookie, setCookie, sharedCookieDomain } from "@medceptor/cookie";

import { ARM_COOKIE_MAX_AGE_SECONDS } from "./premium-paywall";

const HANDOFF_COOKIE = "mc_ph_did";

export type DrawIdentity = {
  distinctId: string;
  deviceId: string;
  handoffCookie: string | null;
};

/** Works only while posthog-js persists its identity to the `ph_<token>_posthog` cookie, not localStorage. */
function persistedIdentity(
  cookieHeader: string | null,
  token: string,
): { distinctId: string; deviceId: string } | null {
  const raw = readCookie(cookieHeader, `ph_${token}_posthog`);
  if (!raw) return null;
  try {
    const props: unknown = JSON.parse(decodeURIComponent(raw));
    if (typeof props !== "object" || props === null) return null;
    const { distinct_id: distinctId, $device_id: deviceId } = props as Record<
      string,
      unknown
    >;
    if (typeof distinctId !== "string" || !distinctId) return null;
    if (typeof deviceId !== "string" || !deviceId) return null;
    return { distinctId, deviceId };
  } catch {
    return null;
  }
}

export function resolveDrawIdentity(input: {
  cookieHeader: string | null;
  token: string;
  hostname: string;
  secure: boolean;
  mint?: () => string;
}): DrawIdentity {
  const persisted = persistedIdentity(input.cookieHeader, input.token);
  if (persisted) return { ...persisted, handoffCookie: null };

  const handedOff = readCookie(input.cookieHeader, HANDOFF_COOKIE);
  if (handedOff) {
    return { distinctId: handedOff, deviceId: handedOff, handoffCookie: null };
  }

  const deviceId = (input.mint ?? (() => crypto.randomUUID()))();
  return {
    distinctId: deviceId,
    deviceId,
    handoffCookie: setCookie(HANDOFF_COOKIE, deviceId, {
      domain: sharedCookieDomain(input.hostname),
      secure: input.secure,
      // Arm cookie's lifetime: a visitor without posthog-js (GPC, DNT) keeps the same id.
      maxAge: ARM_COOKIE_MAX_AGE_SECONDS,
    }),
  };
}

/** posthog-js `get_device_id`: the handed-off id, so exposure and signup are one person. Browser-only. */
export function deviceIdFromHandoff(generated: string): string {
  return takeHandoffDeviceId(document, location) ?? generated;
}

/** Deletes the cookie on read: `reset()` (sign-out) calls `get_device_id` too, and a second signup must not reuse the id. */
export function takeHandoffDeviceId(
  doc: { cookie: string },
  location: { hostname: string; protocol: string },
): string | null {
  const id = readCookie(doc.cookie, HANDOFF_COOKIE);
  if (!id) return null;
  doc.cookie = setCookie(HANDOFF_COOKIE, "", {
    domain: sharedCookieDomain(location.hostname),
    secure: location.protocol === "https:",
    maxAge: 0,
  });
  return id;
}
